import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getTenantConfig } from "@/config/tenantConfig";
import { SubmitHandler, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { Flex, Text, Switch } from "@chakra-ui/react";
import * as yup from "yup";
import { add, format, formatISO } from "date-fns";
import { PaymentMethod } from "../../../layouts/SejaPago/PaymentMethod";
import { formatCurrency, formatCurrencyNoBrSign, formatCurrencyToApi, formatStringAmountToApi, getApplicationName, resolveOpenValueAmount, resolveCreditCardTypeForOrder, resolveCreditCardInstallmentMethod, showToast } from "@/utils";
import {
  InputCurrency,
  Input,
  Select,
  Alert,
  InputSearch,
  AccordionTextArea,
  AccordionCheckbox,
} from "@/components";
import {
  ClientData,
  PaymentMethodTest,
  PunctualChargeProps,
  PaymentLinkSubSellerData,
  SubSellerTaxResponse
} from "@/models";
import { useCustomLink } from "@/hooks";
import { formatPercentage } from "@/utils/formatPercentage";
import { formatPercentageToApi } from "@/utils/formatPercentageToApi";
import { TimeDatePickerInput } from "@/components/Forms/TimeDatePickerInput";
import { CustomFields } from "@/components/Forms/CustomFields";
import { NewContainer } from "@/layouts/SejaPago/NewContainer";
import { SubSellerModal } from "@/components/SubSellersModal";
import { PaymentSplit } from "@/layouts/SejaPago/PaymentSplit";
import { Button } from "barte-design-system";

const paymentMethods: Array<PaymentMethodTest> = [
  { tagName: "creditCard", name: "Cartão de Crédito", reg: "enableCreditCard" },
  { tagName: "bankSlip", name: "Boleto", reg: "enableBankSlip" },
  { tagName: "pix", name: "PIX", reg: "enablePix" },
];

const paymentMethodsLumini: Array<PaymentMethodTest> = [
  { tagName: "creditCard", name: "Cartão de Crédito", reg: "enableCreditCard" },
];

function isFutureDate(dateStr: any): boolean {
  const inputDate = new Date(dateStr);
  const currentDate = new Date();

  return inputDate > currentDate;
}

export interface subSellerData {
  idSubSeller: number;
  id?: number;
  name: string;
  documentType?: string;
  document?: string;
  sellerId?: number;
  isActive?: boolean;
  openingDate?: string;
  paymentType: string;
  paymentValue: number;
  defaultPaymentValue: number;
  defaultPaymentType: string;
  amount?: number;
  description?: string;
  amountOnPaymentLink?: number;
  address?: {
    id: number;
    country: string;
    state: string;
    city: string;
    district: string;
    street: string;
    zipCode: string;
    number: string;
    complement: string;
  };
  account?: {
    id?: number;
    bank?: number;
    bankDigit: string;
    issuer?: string;
    issuerDigit: string;
    number?: string;
    pixKey: string;
    pixKeyType: string;
  };
  contact?: {
    id: number;
    countryCode: string;
    name: string;
    email: string;
    phone: string;
  };
  owner?: {
    ownerName: string;
    ownerDocument: string;
    birthdate?: string;
  };
}

export function PunctualCharge({
  asEdition,
  initialData,
  punctualButton,
  setPunctualButton,
  setPreviewData,
  previewData,
  onSuccess,
}: PunctualChargeProps) {
  const [expirationSwitchState, setExpirationSwitchState] = useState(false);
  const [timerState, setTimerState] = useState(false);
  const [dateSwitchState, setDateSwitchState] = useState(false);
  const [anchoringState, setAnchoringState] = useState(
    initialData?.enableAnchoring ? initialData?.enableAnchoring : false
  );
  const [loading, setLoading] = useState(false);
  const [url, setUrl] = useState("");
  const [clientList, setClientList] = useState<Array<ClientData>>();
  const [selectedClient, setSelectedClient] = useState<ClientData>();
  const [deleteModalState, setDeleteModalState] = useState(false);
  const [metadados, setMetadados] = useState([]);
  const featureTogglesUser = useMemo(() => {
    try {
      const parsed = JSON.parse(localStorage.getItem("barte-features-list-user"));
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }, []);
  const viewMetadados = featureTogglesUser.includes("view_payment_link_metadata");
  const hasOpenValuePaymentLink = featureTogglesUser.includes("open_value_payment_link");
  const [uuid, setUuid] = useState(
    window.location.search.split("?id=").join("")
  );

  const [isOk, setIsOk] = useState(
    {
      geralInformation: false,
      paymentMethods: false,
    }
  );

  function handleChange(name: string, value: any) {

    setValue(name, value)
    setPreviewData((preview) => ({
      ...preview, [name]: value
    }))
  }

  const PaymentOrderSchema = yup
    .object()
    .shape({
      title: yup.string().required("Preenchimento obrigatório!").max(60, 'O título deve ter no máximo 60 caracteres!'),
      value: yup
        .string()
        .nullable()
        .when("openValue", {
          is: true,
          then: yup.string().nullable().notRequired(),
          otherwise: yup.string().required("Preenchimento obrigatório!"),
        }),
      openValue: yup.boolean(),
      installments: yup.number(),
      date: yup.date(),
      bankSlipDescription: yup.string().max(130, 'A descrição do boleto deve ter no máximo 130 caracteres!'),
      timer: yup.date().test("timer", "A data precisa estar no futuro", (value) => {
        if (!expirationSwitchState) {
          return true;
        }
        return isFutureDate(value);
      }),
      enable3ds: yup.bool().default(false),
      requiresLiabilityShift: yup.bool().default(false)
    })
    .test("paymentMethodsValidation", null, (obj) => {
      if (obj.enableBankSlip || obj.enableCreditCard || obj.enablePix) {
        return true;
      }
      return new yup.ValidationError(
        "Selecione ao menos um método de pagamento.",
        null,
        "paymentMethods"
      );
    });

  const {
    postCustomLink,
    handleDeleteCustomLink,
    getCustomLinkData,
    putCustomLink,
    getSplitValue,
  } = useCustomLink();
  const navigate = useNavigate();
  const cancelRef = useRef();

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    control,
    watch,
    getValues
  } = useForm({
    resolver: yupResolver(PaymentOrderSchema),
  });
  const bankSlipDescriptionValue = watch("bankSlipDescription");
  const optionalDescription = watch("description")
  const title = watch("title")
  const value = watch("value")
  const enableCreditCard = watch("enableCreditCard")
  const enableCreditCardPercent = watch("enableCreditCardPercent")
  const customInstallmentsValuesCreditCard = watch("customInstallmentsValuesCreditCard")
  const enablePix = watch("enablePix")
  const enablePixPercent = watch("enablePixPercent")
  const customInstallmentsValuesPix = watch("customInstallmentsValuesPix")
  const enableBankSlip = watch("enableBankSlip")
  const scheduleDate = watch("date")
  const enableBankSlipPercent = watch("enableBankSlipPercent")
  const customInstallmentsValuesBankSlip = watch("customInstallmentsValuesBankSlip")
  const ExpireParsedTimer = watch("timer")
  const [splitModalSubseller, setSplitModalSubseller] = useState<boolean>(false);
  const [subSellersData, setSubSellersData] = useState<Array<subSellerData>>([] as Array<subSellerData>);
  const [subSellerTaxResponse, setSubSellerTaxResponse] = useState<SubSellerTaxResponse>();
  const [split, setSplit] = useState(initialData?.subSellerResponse?.length >= 1)
  const values = getValues()
  const [metadadosDisabled, setMetadadosDisabled] = useState<boolean>(false)
  const [bankSlipDescriptionState, setBankSlipDescriptionState] = useState(false);
  const descriptionValue = watch("description");
  const hasTagMix = localStorage?.getItem("hasMixInstallments") === "true" ? true : false;
  const user = JSON.parse(localStorage.getItem("barte-user-info"));
  const viewSplit = localStorage?.getItem("barte-features-list-user")?.includes("view_split_subseller_payment_link") && user?.roles?.includes("SELLER_ADMIN")
  const sellerDefaultCreditCardType =
    (user?.idSeller === 4350 || user?.idSeller === 4483 || user?.idSeller === 4768 || user?.idSeller === 4770) ? "EARLY_SELLER" : "EARLY";
  const [creditCardType, setCreditCardType] = useState(
    initialData?.allowedPaymentMethods?.creditCardMethod?.type && asEdition
      ? initialData?.allowedPaymentMethods?.creditCardMethod?.type
      : sellerDefaultCreditCardType
  );
  const openValue = !!watch("openValue");
  const creditCardTypeBeforeOpenValue = useRef<string | null>(null);
  const canUseOpenValue = hasOpenValuePaymentLink && Boolean(enableCreditCard) && !hasTagMix;

  function enableOpenValue() {
    creditCardTypeBeforeOpenValue.current = creditCardType;
    setValue("openValue", true);
    setCreditCardType("EARLY_SELLER");
    handleChange("value", "");
    setValue("enableCreditCardPercent", null);
    setValue("enableBankSlipPercent", null);
    setValue("enablePixPercent", null);
    setSplit(false);
    setSubSellersData([]);
  }

  function disableOpenValue() {
    setValue("openValue", false);
    setCreditCardType(creditCardTypeBeforeOpenValue.current ?? sellerDefaultCreditCardType);
    creditCardTypeBeforeOpenValue.current = null;
  }

  function filterCustomInstallments(values: any): Array<any> {
    const filteredCustomInstallments = [
      values.customInstallmentsValuesCreditCard && {
        paymentMethod: resolveCreditCardInstallmentMethod(!!values.openValue, hasTagMix, creditCardType),
        installments: values.customInstallmentsValuesCreditCard
      },
      values.customInstallmentsValuesBankSlip && {
        paymentMethod: "BANK_SLIP",
        installments: values.customInstallmentsValuesBankSlip
      },
      values.customInstallmentsValuesPix && {
        paymentMethod: "PIX",
        installments: values.customInstallmentsValuesPix
      },
    ].filter(item => item && item.installments !== 0)
    return filteredCustomInstallments;
  }
  async function handleSplitValues() {
    try {
      const splitBodyRequest: Array<PaymentLinkSubSellerData> = subSellersData.map(item => {
        //@ts-ignore
        let paymentValue = item?.paymentValue;
        return {
          idSubSeller: item?.id || item?.idSubSeller,
          amount: watch("openValue") ? null : formatCurrencyToApi(watch("value")),
          paymentType: item?.paymentType,
          paymentValue: item?.paymentType === "PERCENTAGE" ? formatPercentageToApi(paymentValue) : formatStringAmountToApi(paymentValue),
        }
      })
      const { data } = await getSplitValue(splitBodyRequest);
      data?.subSellerPaymentResponse?.map(subSRes => {
        const getSubSellerIndex = subSellersData.findIndex(subData => subData?.id === subSRes?.idSubSeller);
        if (getSubSellerIndex !== -1) {
          subSellersData[getSubSellerIndex].amountOnPaymentLink = subSRes?.amountOnPaymentLink;
        }
      })
      //@ts-ignore
      setSubSellerTaxResponse(data);
    } catch (error) {
      error?.response?.data?.errors?.map((error) => {
        showToast({
          type: "error",
          message: `${error?.code} - ${error?.description}`
        })
      });
      return error
    }
  }

  function handleSetOrderValues(data: any) {
    if (data?.scheduledDate) {
      setDateSwitchState(true);
      setValue("date", add(new Date(data?.scheduledDate), { days: 1 }));
    }

    setValue("title", data?.paymentOrder?.title);
    setValue("description", data?.paymentOrder?.description);
    if (data?.bankSlipDescription?.length > 0) {
      setBankSlipDescriptionState(true);
      setValue("bankSlipDescription", data?.bankSlipDescription);
    }
    const orderValue = data?.paymentOrder?.value;
    setValue("openValue", orderValue == null);
    setValue(
      "value",
      orderValue == null ? "" : (formatCurrencyNoBrSign({ value: Number(orderValue) }) || "")
    );
    setValue("installments", data?.paymentOrder?.installments);
    setValue("enableBankSlip", data?.allowedPaymentMethods?.bankSlipMethod);
    setValue("enableCreditCard", data?.allowedPaymentMethods?.creditCardMethod);
    setValue("enablePix", data?.allowedPaymentMethods?.pixMethod);
    setValue(
      "enableCreditCardPercent",
      data?.allowedPaymentMethods?.creditCardMethod?.discount?.amount ?
        formatPercentage({ value: data?.allowedPaymentMethods?.creditCardMethod?.discount?.amount }) :
        "0"
    );
    setValue(
      "enableBankSlipPercent",
      data?.allowedPaymentMethods?.bankSlipMethod?.discount?.amount ?
        formatPercentage({ value: data?.allowedPaymentMethods?.bankSlipMethod?.discount?.amount }) :
        "0"
    );
    setValue(
      "enablePixPercent",
      data?.allowedPaymentMethods?.pixMethod?.discount?.amount ?
        formatPercentage({ value: data?.allowedPaymentMethods?.pixMethod?.discount?.amount }) :
        "0"
    );
    setValue(
      "timer",
      data?.paymentOrder?.expirationDate ? new Date(data?.paymentOrder?.expirationDate) : new Date()
    );
    setExpirationSwitchState(!!data?.paymentOrder?.expirationDate);
    setTimerState(data?.paymentOrder?.timer);
    setUrl(data.url);
    setAnchoringState(data?.enableAnchoring);
    setValue("enable3ds", data?.paymentOrder?.threeDSecureEnabled ?? false);
    setValue("requiresLiabilityShift", data?.paymentOrder?.requiresLiabilityShift ?? false);
    const formattedSubSellerData = initialData?.subSellerResponse?.map(subS => {
      return {
        ...subS,
        defaultPaymentValue: subS.paymentValue,
        defaultPaymentType: subS.paymentType,
      }
    })
    setSubSellersData(formattedSubSellerData);
    setUuid(data.uuid);
    data?.paymentOrder?.customInstallmentsValues?.map(item => {
      if (item?.paymentMethod?.includes("CREDIT_CARD")) {
        setValue("customInstallmentsValuesCreditCard", item?.installments)
      }
      if (item?.paymentMethod?.includes("BANK_SLIP")) {
        setValue("customInstallmentsValuesBankSlip", item?.installments)
      }
      if (item?.paymentMethod?.includes("PIX")) {
        setValue("customInstallmentsValuesPix", item?.installments)
      }
    });
    if (initialData?.subSellerResponse?.length >= 1) {
      setSplit(true)
    }
  }
  async function handleGetDataToDuplicate(uuid: string) {
    try {
      const { data } = await getCustomLinkData(uuid);
      handleSetOrderValues(data);
    } catch (error) {

      error?.response?.data?.errors.map((error) => {
        showToast({
          type: "error",
          message: `${error?.code} - ${error?.description}`
        })
      });
    }
  }

  async function getInitialData() {
    setValue("date", new Date());
    if (initialData) return handleSetOrderValues(initialData);
    if (uuid) {
      try {
        handleGetDataToDuplicate(uuid);
        return;
      } catch (error) {
        error?.response?.data?.errors.map((error) => {
          showToast({
            type: "error",
            message: `${error?.code} - ${error?.description}`
          })
        });
      }
    }
    if (user?.idSeller === 4483) {
      setValue("installments", 6)
    } else {
      setValue("installments", 12)
    }
  }

  const handleCreatePaymentOrder: SubmitHandler<any> = async (
    values
  ) => {
    setLoading(true);
    const parsedDate =
      dateSwitchState && formatISO(values?.date, { representation: "date" });
    const parsedTimer = expirationSwitchState && formatISO(values?.timer, { representation: "complete" }).split("-03:00").at(0);
    const formattedValue = resolveOpenValueAmount(!!values?.openValue, values?.value);
    const subSellersDataFilter = subSellersData?.map((item) => ({
      idSubSeller: item?.id || item?.idSubSeller,
      paymentType: item?.paymentType,
      paymentValue: item?.paymentValue,
      amount: item?.amountOnPaymentLink
    })).filter(sub => sub?.idSubSeller !== undefined);
    const applicationName = getApplicationName({});
    const paymentOrderNewDTO = {
      type: "ORDER",
      whiteLabel: applicationName !== "barte" ? true : false,
      scheduledDate: dateSwitchState ? parsedDate : null,
      uuidSellerClient: selectedClient?.uuid,
      bankSlipDescription: (bankSlipDescriptionState && values?.bankSlipDescription?.length > 0) ? values?.bankSlipDescription : null,
      metadata: metadadosDisabled ? metadados : null,
      subSellerPaymentRequest: split ? subSellersDataFilter : [],
      paymentOrder: {
        title: values?.title,
        description: values?.description,
        installments: 10,
        value: formattedValue,
        customInstallmentsValues: filterCustomInstallments(values),
        expirationDate: parsedTimer ? parsedTimer : null,
        timer: parsedTimer ? timerState : null,
        threeDSecureEnabled: values?.enable3ds ?? false,
        requiresLiabilityShift: (values?.enable3ds && values?.requiresLiabilityShift) ?? false,
      },
      enableAnchoring: anchoringState,
      allowedPaymentMethods: {
        creditCardMethod: values.enableCreditCard
          ? {
            type: resolveCreditCardTypeForOrder(!!values.openValue, hasTagMix, creditCardType),
            discount: (!values.openValue && values.enableCreditCardPercent && formatPercentageToApi(values?.enableCreditCardPercent) > 0)
              ? {
                amount: formatPercentageToApi(values?.enableCreditCardPercent),
                type: "PERCENT",
              }
              : null,
          }
          : null,
        bankSlipMethod: values.enableBankSlip
          ? {
            discount: (values.enableBankSlipPercent && formatPercentageToApi(values.enableBankSlipPercent) > 0)
              ? {
                amount: formatPercentageToApi(values.enableBankSlipPercent),
                type: "PERCENT",
              }
              : null,
          }
          : null,
        pixMethod: values.enablePix
          ? {
            discount: (values.enablePixPercent && formatPercentageToApi(values.enablePixPercent) > 0)
              ? {
                amount: formatPercentageToApi(values.enablePixPercent),
                type: "PERCENT",
              }
              : null,
          }
          : null,
      }
    };

    try {
      if (asEdition) {
        await putCustomLink(initialData?.uuid, paymentOrderNewDTO);
        const createdUrl = `${getTenantConfig().checkoutUrlV2}payment-link/${initialData?.uuid}`;
        setUrl(createdUrl);
        onSuccess?.(createdUrl);
      } else {
        const { data } = await postCustomLink(paymentOrderNewDTO);
        const createdUrl = `${getTenantConfig().checkoutUrlV2}payment-link/${data?.uuid}`;
        setUrl(createdUrl);
        onSuccess?.(createdUrl);
      }
    } catch (error) {
      error?.response?.data?.errors?.map((error) => {
        showToast({
          type: "error",
          message: `${error?.code} - ${error?.description}`
        })
      });
    }
    setLoading(false);
  };

  async function handleDelete() {
    try {
      const response = await handleDeleteCustomLink(uuid);
      showToast({
        message: "Link customizado excluído com sucesso!",
        type: "success",
      });
      setDeleteModalState(!deleteModalState);
      navigate("/seja-pago");
    } catch (error) {
      error?.response?.data?.errors.map((error) => {
        showToast({
          type: "error",
          message: `${error?.code} - ${error?.description}`
        })
      });
    }
  }

  useEffect(() => {
    getInitialData();
  }, []);

  useEffect(() => {
    if (!canUseOpenValue && openValue && creditCardTypeBeforeOpenValue.current) {
      disableOpenValue();
    }
  }, [canUseOpenValue, openValue]);

  useEffect(() => {


    const hasValidValue = openValue || value !== "";
    if (hasValidValue && title !== "") {
      setIsOk(() => ({
        ...isOk,
        geralInformation: true
      }));
    }
    if (!hasValidValue || title === "") {
      setIsOk(() => ({
        ...isOk,
        geralInformation: false
      }));
    }

    if ((enableBankSlip === true || enablePix === true)) {
      setSplit(false);
    }

    if (
      previewData?.enablePix === true ||
      previewData?.enableCreditCard === true ||
      previewData?.enableBankSlip === true
    ) {
      setIsOk({
        ...isOk,
        paymentMethods: true
      })
    } if (
      previewData?.enablePix === false &&
      previewData?.enableCreditCard === false &&
      previewData?.enableBankSlip === false
    ) {
      setIsOk({
        ...isOk,
        paymentMethods: false
      })
    }
  }, [previewData]);



  useEffect(() => {

    const updatedGeralInformation = {
      ...previewData,
      title: title,
      custonFields: { metadados },
      cliente: selectedClient?.name,
      hasDescription: bankSlipDescriptionState,
      description: optionalDescription,
      value: value,
      subSellersData: subSellersData,
      enablePix: enablePix,
      enableCreditCard: enableCreditCard,
      enableBankSlip: enableBankSlip,
      boleto: {
        discount: enableBankSlipPercent ?? '0%',
        installments: customInstallmentsValuesBankSlip,

      },
      creditcard: {
        discount: enableCreditCardPercent ?? '0%',
        scheduleDate: dateSwitchState ? format(scheduleDate, 'dd/MM/yyyy') : "",
        tax: creditCardType?.tax,
        installments: customInstallmentsValuesCreditCard,
      },
      pix: {
        discount: enablePixPercent ?? '0%',
        tax: customInstallmentsValuesPix,
        installments: customInstallmentsValuesPix,

      },
      expireDate: expirationSwitchState ? format(ExpireParsedTimer, 'dd/MM/yyyy HH:mm:ss') : ""
    };
    setPreviewData(updatedGeralInformation);

  }, [metadados, selectedClient, bankSlipDescriptionValue, optionalDescription,
    title, value, enableCreditCard, enableCreditCardPercent,
    customInstallmentsValuesCreditCard, enablePix, enablePixPercent, customInstallmentsValuesPix,
    enableBankSlip, customInstallmentsValuesBankSlip, ExpireParsedTimer, creditCardType, scheduleDate,
    subSellersData, expirationSwitchState, dateSwitchState, bankSlipDescriptionState, enableBankSlipPercent
  ]);



  useEffect(() => {
    if (split === true) {
      setValue("enableBankSlip", false);
      setValue("enablePix", false);
      setValue('enableCreditCard', true);
    }
  }, [split])



  useEffect(() => {
    setPreviewData({
      title: '',
      custonFields: {},
      cliente: '',
      hasDescription: false,
      description: '',
      value: '',
      enablePix: '',
      enableCreditCard: '',
      enableBankSlip: '',
      boleto: {
        discount: '',
        installments: '',

      },
      creditcard: {
        discount: '',
        scheduleDate: '',
        tax: '',
        installments: '',
      },
      pix: {
        discount: '',
        tax: '',
        installments: '',

      },
      expireDate: ''
    })
  }, [punctualButton])

  return (
    <>

      <Alert
        isConfirmAlert={false}
        isOpen={deleteModalState}
        onConfirm={handleDelete}
        onClose={() => setDeleteModalState(!deleteModalState)}
        title={`Tem certeza que deseja excluir o link para essa cobrança pontual?`}
        messageBody="Excluí-lo desativará o link para futuras contratações."
        closeButtonText="Não"
        confirmButtonText="Sim"
        leastDestructiveRef={cancelRef}
      />
      {viewSplit && (
        <SubSellerModal
          isOpen={splitModalSubseller}
          headerText="Revise os valores do split"
          totalValue={value}
          paymentLinkValue={watch("value") ?? "0"}
          subSellerList={subSellersData}
          confirmButtonText="Criar link"
          closeButtonText="Voltar"
          subSellerTaxResponse={subSellerTaxResponse}
          barteTaxValue={previewData?.creditCardTax === "EARLY_SELLER" ? formatCurrency({ value: (formatCurrencyToApi(watch("value")) - previewData?.arrayInstallments?.installmentsToSeller[previewData?.arrayInstallments?.installmentsToSeller?.length - 1]?.receivedAmount) ?? 0 }) : formatCurrency({ value: (formatCurrencyToApi(watch("value")) - previewData?.arrayInstallments?.installmentsToSeller[0]?.receivedAmount) ?? 0 })}
          onClose={() => setSplitModalSubseller(false)}
          onConfirm={() => handleCreatePaymentOrder(values)}
          asSubmitModal
        />
      )}
    
      <Flex
        as="form"
        w="100%"
        flexDir="column"
        onSubmit={handleSubmit(handleCreatePaymentOrder)}
        autoComplete="off"
      >
  
        <NewContainer
          type="check"
          w="100%"
          title="Informações gerais"
          startsOpen
          isOk={(openValue || value !== "") && title !== ""}
          p="16px"
          borderColor="#E6E9F0"
          backgroundColor="#fff"
          borderRadius="8px"
          border="1px solid #E6E9F0"
          padding="16px"
        >


          <Input
            label="Nome do link"
            type="text"
            placeholder="Nome do link"
            name="title"
            register={{ ...register("title") }}
            onChange={(e) => {
              handleChange("title", e.target.value)
            }}
            errors={errors?.title}
          />
          <Input
            type="textarea"
            label="Descrição do link (opcional)"
            name="description"
            placeholder="Descrição do link"
            color="black"
            labelFontSize=".8rem"
            labelColor="#000000"
            maxLength={300}
            register={{ ...register("description") }}
            currentLength={optionalDescription?.length}
          />
   

          <Flex flexDir="column" gap=".5rem">
            <InputCurrency
              label="Valor"
              setValue={setValue}
              labelColor="#000000"
              name={"value"}
              maxLength={10}
              register={{ ...register("value") }}
              onChange={(e) => {
                handleChange("value", e.target.value)
              }}
              placeholder="Valor"
              containerDisabled={!openValue}
            />
            {hasOpenValuePaymentLink && (
              <Flex flexDir="column" gap="4px">
                <Flex align="center" gap="8px">
                  <Switch
                    size="sm"
                    colorScheme="cyan"
                    isChecked={openValue}
                    isDisabled={!canUseOpenValue}
                    onChange={(e) => e.target.checked ? enableOpenValue() : disableOpenValue()}
                  />
                  <Text fontSize=".85rem" color="gray.700">
                    Cliente define o valor no checkout
                  </Text>
                </Flex>
                {!enableCreditCard && (
                  <Text fontSize=".75rem" color="gray.500">
                    Habilite o Cartão de Crédito para usar valor aberto.
                  </Text>
                )}
                {openValue && (
                  <Text fontSize=".75rem" color="gray.500">
                    As taxas de parcelamento passam a ser assumidas pelo vendedor.
                  </Text>
                )}
              </Flex>
            )}
          </Flex>


          <InputSearch
            name="clients"
            label="Clientes"
            labelColor="#000000"
            placeholder="Nome do cliente"
            initialClient={initialData?.sellerClient}
            clients={clientList}
            setClients={setClientList}
            selectedClient={selectedClient}
            setSelectedClient={setSelectedClient}
          />
        </NewContainer>
     

        <NewContainer
          w="100%"
          type="check"
          padding="0px 16px 0 16px"
          title="Métodos de pagamento"
          paddingTitle=" 16px 0 0 16px"
          isOk={previewData?.enableBankSlip || previewData?.enableCreditCard || previewData?.enablePix}
        >
          <PaymentMethod
            setIsOk={setIsOk}
            initialData={initialData}
            control={control}
            onCreation={!asEdition}
            hasInstallmentMix={Boolean(hasTagMix)}
            creditCardType={creditCardType}
            setCreditCardType={setCreditCardType}
            register={register}
            data={user.idSeller === 4483 ? paymentMethodsLumini : paymentMethods}
            errors={errors?.paymentMethods}
            watch={watch}
            setValue={setValue}
            anchoringState={anchoringState}
            isPunctualCharge={true}
            setAnchoringState={setAnchoringState}
            dateSwitchState={dateSwitchState}
            setDateSwitchState={setDateSwitchState}
            split={split}
            disableDiscount={openValue}
            disableCardTypeSelection={openValue}
          />
        </NewContainer>
     
        <NewContainer
          w="100%"
          title="Configurações avançadas"
          type="retract"
          p="16px"
          borderColor="#E6E9F0"
          backgroundColor="#fff"
          borderRadius="8px"
          border="1px solid #E6E9F0"
          padding="16px"
        >

          <TimeDatePickerInput
            timerState={timerState}
            setTimerState={setTimerState}
            startEnabled
            label="Expirar Link"
            name="timer"
            register={{ ...register("timer") }}
            hasSwitch
            control={control}
            disabledState={expirationSwitchState}
            setDisabledState={setExpirationSwitchState}
            errors={errors?.timer}
            setValue={setValue}
          />

          <AccordionTextArea
            name="bankSlipDescription"
            hasSwitch={bankSlipDescriptionState}
            register={{ ...register("bankSlipDescription") }}
            value={bankSlipDescriptionValue}
            errors={errors?.bankSlipDescription}
            asDoubleEffect
            switchFunction={(e) => setBankSlipDescriptionState(e)}
          />

          {viewMetadados && (
            <CustomFields
              setMetadados={setMetadados}
              defaultMetadados={initialData?.metadata}
              hasSwitch
              setMetadadosDisabled={setMetadadosDisabled}
              name="Dados internos (metadados)"
              label="Dados internos"
            />
          )}

          {viewSplit && !openValue && (
            <PaymentSplit
              setSplit={setSplit}
              split={split}
              title={"Split de pagamento"}
              setSubSellersData={setSubSellersData}
              subSellersData={subSellersData}
              newClient={{} as subSellerData}
            />
          )}

          <AccordionCheckbox
            primaryLabel={<>Incluir verificação via 3DS <Text as="em" color="#767676" fontStyle="italic" whiteSpace="nowrap" display="inline">(para "Cartão de Crédito")</Text></>}
            secondaryLabel="Exigir liability shift"
            primaryName="enable3ds"
            secondaryName="requiresLiabilityShift"
            setValue={setValue}
            watch={watch}
            mt="24px"
          />


        </NewContainer>
   
        <Flex
         justifyContent="end"
          padding="16px"
          maxW="740px"
        >
          <Button
            type={split && viewSplit && !openValue ? "button" : "submit"}
            loading={loading}
            onClick={() => {
              if (split && viewSplit && !openValue) {
                handleSplitValues()
                setSplitModalSubseller(!splitModalSubseller)
              }
            }}
            style={{ margin: "1rem 0" }}
            disabled={!((openValue || value !== "") && title !== "") || !(enableBankSlip || enableCreditCard || enablePix)}

          >
            {asEdition ? "Salvar" : "Criar cobrança"}
          </Button>
        </Flex>
      </Flex>
    </>
  );
}
